import {
  Component,
  Input,
  OnInit,
  ViewChild,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { calendarColors } from 'app/modules/calendar/sidebar/calendar-colors';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { TemplatePortal } from '@angular/cdk/portal';
import orderBy from 'lodash-es/orderBy';
import { AccountService } from 'app/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AccountState } from '@state/account/state';
import { Account, AccountColor, AccountSettings } from '@core/models';
import { RelatedToType } from '@shared/enums';
import { ObjectsManagerState } from '../../objects-manager/state/state';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { AccountActions } from '@state/account/actions';
import { filter } from 'rxjs';

@Component({
  selector: 'settings-stages-sortable-dropdown-editor',
  templateUrl: './stages-sortable-dropdown-editor.component.html',
  styleUrls: ['./stages-sortable-dropdown-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StagesSortableDropdownEditorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() options: any[];
  @Input() pipelines: any[];
  @Input() title: string;
  @Input() showColor: boolean = false;
  @Input() showRequiredFields: boolean = false;
  @Input() relatedToType: RelatedToType;
  @Output() onUpdate: EventEmitter<any> = new EventEmitter();
  @Output() onAdd: EventEmitter<any> = new EventEmitter();
  @Output() rankChanged = new EventEmitter();

  numOptions: number[] = [];

  accountColors: AccountColor[] = [];
  account: Account;
  dropdownForm: UntypedFormGroup;
  calendarColors: any;
  newCreatedAccountColorHex: string = '';
  updatedOption: any = null;

  private _pipelinesPanelOverlayRef: OverlayRef;
  @ViewChild('pipelinesPanel')
  private _pipelinesPanel: TemplateRef<any>;
  @ViewChild('pipelinesPanelOrigin')
  private _pipelinesPanelOrigin: MatButton;
  private _pipelinesPanelTemplatePortal: TemplatePortal;
  public fieldDefs: any;
  isColorPickerOpen: boolean = false;

  /**
   * Constructor
   *
   * @param {FormBuilder} _formBuilder
   */
  constructor(
    private _accountService: AccountService,
    private _formBuilder: UntypedFormBuilder,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private store: Store,
    private actions: Actions,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.calendarColors = calendarColors;

    this.renderOptions();

    this._accountService.getFieldDefs().subscribe((fields) => {
      if (this.relatedToType) {
        this.fieldDefs = this.store.selectSnapshot(
          ObjectsManagerState.getFieldsByObjectName(RelatedToType.Project, {
            allowedFieldsOnly: true,
            addNestedFields: false,
          }),
        );
        return;
      }
      this.fieldDefs = fields;
    });

    const account = this.store.selectSnapshot(AccountState.getAccount);
    this.account = account;
    this.accountColors = account?.colors ?? [];

    this.actions
      .pipe(
        ofActionSuccessful(AccountActions.UpdateAccount),
        switchMap(() => this.store.select(AccountState.getAccount)),
        filter(() => this.newCreatedAccountColorHex && this.updatedOption),
      )
      .subscribe((updatedAccount) => {
        const newColors = updatedAccount?.colors ?? [];
        const newColorId = newColors.filter((item) => item.hex_value === this.newCreatedAccountColorHex)?.[0]?.id ?? -1;
        if (newColorId >= 0) {
          const accountColorIndex = this.accountColors.findIndex(
            (item) => item.hex_value === this.newCreatedAccountColorHex,
          );
          if (accountColorIndex >= 0) {
            this.accountColors[accountColorIndex].id = newColorId;
          }
          this.updatedOption.get('color').setValue(`account:${newColorId}`);
          this.onUpdate.emit(this.updatedOption.value);
          this.updatedOption = null;
          this.newCreatedAccountColorHex = '';
        }
      });
  }

  renderOptions() {
    this.dropdownForm = this._formBuilder.group({
      options: this._formBuilder.array([]),
      newOption: this._formBuilder.group({
        name: ['', Validators.required],
      }),
    });
    this.options = orderBy(this.options, 'rank', 'asc');
    this.options.forEach((option) => {
      const formGroup = this._formBuilder.group({
        id: [option.id],
        name: [option.name, Validators.required],
        hidden: [option.hidden],
        rank: [option.rank],
        color: [option.color || 'gray'],
        pipeline_ids: [option.pipeline_ids],
        required_fields: [option.required_fields?.split(',') ?? []],
      });

      // Add the label form group to the labels form array
      (this.dropdownForm.get('options') as UntypedFormArray).push(formGroup);
    });

    this.numOptions = [];
    for (let i = 0; i < this.options.length; i++) {
      this.numOptions.push(i);
    }

    this.dropdownForm
      .get('options')
      .valueChanges.pipe(debounceTime(500))
      .subscribe(() => {
        this.updateOptions();
      });
  }

  ngOnChanges() {
    this.renderOptions();
  }

  /**
   * Update labels
   */
  updateOptions(): void {
    // Iterate through the labels form array controls
    (this.dropdownForm.get('options') as UntypedFormArray).controls.forEach((formGroup) => {
      // If the label has been edited...
      if (formGroup.dirty) {
        // Update the label on the server
        let required_fields = formGroup.value.required_fields?.join(',');
        let option = {
          id: formGroup.value.id,
          name: formGroup.value.name,
          hidden: formGroup.value.hidden,
          rank: formGroup.value.rank,
          pipeline_ids: formGroup.value.pipeline_ids,
          required_fields: required_fields,
          color: formGroup.value.color,
        };
        this.onUpdate.emit(option);
      }
    });

    // Reset the labels form array
    this.dropdownForm.get('options').markAsPristine();
    this.dropdownForm.get('options').markAsUntouched();
  }

  ngOnDestroy() {}

  showHideOption(formGroup): void {
    let option = formGroup.value;
    option.hidden = !option.hidden;
    this.onUpdate.emit(option);
    // Reset the labels form array
    formGroup.get('hidden').setValue(option.hidden);
  }

  /**
   * Add a Pipeline
   */
  addOption(): void {
    const val = this.dropdownForm.get('newOption').value;
    this.onAdd.emit(val);

    /*(this.dropdownForm.get('options') as FormArray).push(this._formBuilder.group({
        id   : [val.id],
        name: [val.name, Validators.required],
    }));*/
    // Reset the labels form array
    this.dropdownForm.get('newOption').markAsPristine();
    this.dropdownForm.get('newOption').markAsUntouched();
    this.dropdownForm.get('newOption.name').reset();
    this.dropdownForm.get('newOption.name').clearValidators();
    this.dropdownForm.get('newOption.name').updateValueAndValidity();
  }

  openPipelinesPanel(): void {
    // Create the overlay
    this._pipelinesPanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._pipelinesPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Subscribe to the attachments observable
    this._pipelinesPanelOverlayRef.attachments().subscribe(() => {
      // Add a class to the origin
      //this._renderer2.addClass(this._fieldDefsPanelOrigin.nativeElement, 'panel-opened');

      // Focus to the search input once the overlay has been attached
      this._pipelinesPanelOverlayRef.overlayElement.querySelector('input').focus();
    });

    // Create a portal from the template
    this._pipelinesPanelTemplatePortal = new TemplatePortal(this._pipelinesPanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._pipelinesPanelOverlayRef.attach(this._pipelinesPanelTemplatePortal);

    // Subscribe to the backdrop click
    this._pipelinesPanelOverlayRef.backdropClick().subscribe(() => {
      this._closePipelinesPanel();
    });
  }

  /**
   * Close the event panel
   *
   * @private
   */
  private _closePipelinesPanel(): void {
    // Remove the class from the origin
    //this._renderer2.removeClass(this._fieldDefsPanelOrigin.nativeElement, 'panel-opened');

    // If overlay exists and attached...
    if (this._pipelinesPanelOverlayRef && this._pipelinesPanelOverlayRef.hasAttached()) {
      // Detach it
      this._pipelinesPanelOverlayRef.detach();

      // Reset the tag filter
      //this.filteredUserAssociations = this.accountUserAssociations;
    }

    // If template portal exists and attached...
    if (this._pipelinesPanelTemplatePortal && this._pipelinesPanelTemplatePortal.isAttached) {
      // Detach it
      this._pipelinesPanelTemplatePortal.detach();
    }
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.options, event.previousIndex, event.currentIndex);
    this.rankChanged.emit(this.options);
    this.rerank(this.options);
  }

  openColorPicker(colorInput: any) {
    colorInput?.click?.();
  }

  onColorChange(event: Event, option: any) {
    const input = event.target as HTMLInputElement;
    const selectedColor = input.value;

    this.newCreatedAccountColorHex = selectedColor;
    this.updatedOption = option;

    this.store.dispatch(
      new AccountActions.UpdateAccount({
        id: this.account.id,
        colors: [
          ...this.accountColors,
          {
            color_type: 'secondary',
            hex_value: selectedColor,
            label: selectedColor,
            name: selectedColor,
          },
        ] as AccountColor[],
      } as AccountSettings),
    );
    this.accountColors = [
      ...this.accountColors,
      {
        color_type: 'secondary',
        hex_value: selectedColor,
        label: selectedColor,
        name: selectedColor,
      } as AccountColor,
    ];
  }

  rerank(options) {
    options.forEach((e, i) => (e.rank = i));
    this.renderOptions();
  }

  getAccountColor(option: any): string {
    const colorValue = option.get('color').value;
    const colorId = parseInt(colorValue.replace('account:', ''));
    const colorHex = this.accountColors.filter((item) => item.id === colorId)?.[0]?.hex_value ?? '';
    return colorHex;
  }

  isAccountColor(option: any): boolean {
    const colorValue = option.get('color').value;
    return colorValue?.indexOf?.('account:') >= 0;
  }
}
